import { SubscriptionType } from "@/types/enums/SubscriptionType";
import { SubscriptionScope } from "@/types/enums/SubscriptionScope";
import type { BillingPrices, CouponValidation } from "@/types/Billing";
import type { Subscription } from "@/types/Subscription";

/**
 * Shared catalog for the purchasable per-event plans. Single source for the
 * subscription page (PlanSection) and the create-event wizard's plan step so
 * the two surfaces can't drift on item types, badge styling or fallback
 * prices.
 */

export const EVENT_PAID_PLANS = [
  SubscriptionType.EVENT_ESSENTIAL,
  SubscriptionType.EVENT_ADVANCED,
  SubscriptionType.EVENT_PROFESSIONAL,
] as const;

/**
 * PB-EDP-1: whether an event's `planType` names a paid event plan — the
 * condition for the event-delete dialogs' notice. Type only, on purpose: the
 * list rows carry no lock or expiry, and `planType` stays paid after the
 * 9-month expiry, so the notice is worded state-neutrally ("if it is still
 * running"). EVENT_FREE and a missing type are no paid plan.
 */
export function hasPaidEventPlan(planType?: string | null): boolean {
  return !!planType && (EVENT_PAID_PLANS as readonly string[]).includes(planType);
}

/** Maps SubscriptionType enum to the itemType expected by the purchase API. */
export const EVENT_PLAN_ITEM_TYPE: Record<string, string> = {
  [SubscriptionType.EVENT_ESSENTIAL]: "event_essential",
  [SubscriptionType.EVENT_ADVANCED]: "event_advanced",
  [SubscriptionType.EVENT_PROFESSIONAL]: "event_professional",
};

/**
 * Cross-scope tier rank — company and per-event plans on one 0-3 scale. Mirrors
 * `BillingConfig.getTierRank` on the backend. Anything unknown ranks 0 so a new
 * enum value can never silently hide every plan card.
 */
const TIER_RANK: Record<string, number> = {
  [SubscriptionType.FREE]: 0,
  [SubscriptionType.FREE_YEAR]: 0,
  [SubscriptionType.EVENT_FREE]: 0,
  [SubscriptionType.ESSENTIAL]: 1,
  [SubscriptionType.ESSENTIAL_YEAR]: 1,
  [SubscriptionType.EVENT_ESSENTIAL]: 1,
  [SubscriptionType.ADVANCED]: 2,
  [SubscriptionType.ADVANCED_YEAR]: 2,
  [SubscriptionType.EVENT_ADVANCED]: 2,
  [SubscriptionType.PROFESSIONAL]: 3,
  [SubscriptionType.PROFESSIONAL_YEAR]: 3,
  [SubscriptionType.EVENT_PROFESSIONAL]: 3,
};

export function tierRank(type?: string | null): number {
  return type ? (TIER_RANK[type] ?? 0) : 0;
}

/**
 * Picks the company's own base-plan tier out of a subscription list.
 *
 * The company tier is NOT a field on `Company` — neither the DTO nor the
 * backend entity carries one (`useCompany`'s "plan" projection resolves to
 * nothing). The only source of truth is the COMPANY-scope subscription row, so
 * callers must request `scope` in the projection. No row = FREE.
 *
 * The add-on type is skipped: it rides the same company scope but is a seat
 * add-on, not a tier.
 */
export function companyTierFromSubscriptions(
  subscriptions: Subscription[] | null | undefined,
): SubscriptionType | null {
  if (!subscriptions) return null;
  // W4 (2026-08-02): the HIGHEST company row wins, not whichever the backend
  // happened to emit first. `.find()` over an unordered array made the offered
  // plan list depend on Mongo's natural order: with a leftover FREE row beside a
  // live PROFESSIONAL — the realistic pair — a FREE win offered all three event
  // plans, and the backend then rejected the purchase with
  // `eventPlanNotAboveCompanyPlan`, which is the exact error this filter exists
  // to prevent the user from meeting. Mirrors
  // `SubscriptionRepository.findCompanyBasePlan`.
  let best: SubscriptionType | null = null;
  for (const s of subscriptions) {
    if (
      s.scope !== SubscriptionScope.COMPANY ||
      !s.type ||
      s.type === SubscriptionType.ADDITIONAL_COLLABORATOR_COMPANY
    ) {
      continue;
    }
    if (best === null || tierRank(s.type) > tierRank(best)) best = s.type;
  }
  return best;
}

/**
 * The per-event plans worth offering. A plan is only shown when it ranks ABOVE
 * everything the event already benefits from — its own purchased plan AND the
 * company's base plan.
 *
 * Feature gating is a UNION of both scopes on the backend
 * (`SubscriptionFeatureGate.hasProfessionalInList(eventSubs) || ...(companySubs)`),
 * so a lower or equal tier adds nothing at all: it only arms the 9-month event
 * lock, and before the MAX fix it also cut the event's seat count. A
 * PROFESSIONAL company therefore gets an empty list — there is nothing left to
 * sell. The backend refuses these sales too (`eventPlanNotAboveCompanyPlan`);
 * this filter is what keeps the user from meeting that error.
 */
export function offeredEventPlans(
  companyTier?: SubscriptionType | string | null,
  eventPlanType?: SubscriptionType | string | null,
): SubscriptionType[] {
  const floor = Math.max(tierRank(companyTier), tierRank(eventPlanType));
  return EVENT_PAID_PLANS.filter((plan) => tierRank(plan) > floor);
}

export const EVENT_PLAN_BADGE_COLORS: Record<string, string> = {
  [SubscriptionType.EVENT_ESSENTIAL]: "plan-badge-essential border",
  [SubscriptionType.EVENT_ADVANCED]: "plan-badge-advanced border",
  [SubscriptionType.EVENT_PROFESSIONAL]: "plan-badge-professional border",
};

// Display-only fallback when the /prices call has not resolved (the backend
// remains the source of truth for what is actually charged). EUR cents — must
// mirror the Stripe event price ids in eb-env.tf; a stale value here shows the
// user a price they will not be charged.
export const EVENT_PLAN_PRICE_CENTS: Record<string, number> = {
  [SubscriptionType.EVENT_ESSENTIAL]: 22900,
  [SubscriptionType.EVENT_ADVANCED]: 67500,
  [SubscriptionType.EVENT_PROFESSIONAL]: 127000,
};

/**
 * Mails an event licence grants, once per purchase, into the company's
 * non-expiring balance (`Company.purchasedEmails`, spent after the plan's
 * mails, never reset). Display mirror of main-backend
 * `BillingService.emailGrantFor` and main-consumer `EMAIL_GRANT`
 * (billing-service.ts), which grant it — keep the three identical. EVENT_FREE
 * grants none. An upgrade grants only the difference: `eventPlanEmailGrant`.
 * Pinned in event-plan-email-grant.test.ts.
 */
export const EVENT_EMAIL_GRANT: Record<string, number> = {
  [SubscriptionType.EVENT_ESSENTIAL]: 5_000,
  [SubscriptionType.EVENT_ADVANCED]: 20_000,
  [SubscriptionType.EVENT_PROFESSIONAL]: 50_000,
};

/**
 * The mails buying `plan` grants for an event whose subscription ROW holds
 * `rowType` — what its card must promise (audit-purchased-emails MED-4).
 *
 * Mirrors main-backend: the purchase is an upgrade exactly when the event's
 * row holds a paid event plan (`eventTypeToItemType(existingSub.getType()) !=
 * null`, the same rule as `eventUpgradeBaseline` above), and the upgrade stamp
 * is `upgradeEmailGrant` = max(0, new − old). A licence bought before the
 * activation granted nothing and still upgrades by the difference only, so
 * the card says "+N" with that N, never the new plan's full figure. Any other
 * purchase is a first one and grants the full `EVENT_EMAIL_GRANT`.
 */
export function eventPlanEmailGrant(
  plan: string,
  rowType: string | null | undefined,
): { emails: number; upgrade: boolean } {
  const full = EVENT_EMAIL_GRANT[plan] ?? 0;
  const upgrade = !!rowType && (EVENT_PAID_PLANS as readonly string[]).includes(rowType);
  if (!upgrade) return { emails: full, upgrade: false };
  return { emails: Math.max(0, full - (EVENT_EMAIL_GRANT[rowType] ?? 0)), upgrade: true };
}

export function getEventPlanPriceCents(
  plan: string,
  prices: BillingPrices,
): number {
  switch (plan) {
    case SubscriptionType.EVENT_ESSENTIAL:
      return prices.eventEssential;
    case SubscriptionType.EVENT_ADVANCED:
      return prices.eventAdvanced;
    case SubscriptionType.EVENT_PROFESSIONAL:
      return prices.eventProfessional;
    default:
      return 0;
  }
}

/** The event's own subscription row, as far as the upgrade credit needs it. */
export interface EventPlanRow {
  type?: string;
  amountPaid?: number | null;
  currency?: string | null;
}

/**
 * The amount an event-plan UPGRADE credits, mirroring main-backend
 * `handleEventPlanPurchase` + `resolveUpgradeBaselineCents` (Stripe go-live
 * re-audit r2/r3), keyed on the event's subscription ROW (`type`, not
 * `event.planType`):
 *  - no row, or a row of no paid event type ⇒ the backend sells a first
 *    purchase at full price: nothing credited;
 *  - the stored `amountPaid` when it is > 0 and in the charge currency (an
 *    absent stored or charge currency matches, as on the backend) ⇒ what was
 *    PAID;
 *  - otherwise (null since the currency-amount fix, a cemented 0 — also a
 *    100 %-off purchase — or another currency) ⇒ the row's plan LIST price,
 *    credited but not "paid".
 * `cents` is `undefined` when the catalog omitted that list price.
 */
export function eventUpgradeBaseline(
  row: EventPlanRow | null | undefined,
  prices: BillingPrices | null,
): { cents: number | undefined; paid: boolean } {
  if (!row?.type || !(EVENT_PAID_PLANS as readonly string[]).includes(row.type)) {
    return { cents: 0, paid: false };
  }
  const stored = row.amountPaid;
  const currencyMatches =
    !row.currency || !prices?.currency || row.currency.toLowerCase() === prices.currency.toLowerCase();
  if (typeof stored === "number" && Number.isFinite(stored) && stored > 0 && currencyMatches) {
    return { cents: stored, paid: true };
  }
  return {
    cents: prices ? getEventPlanPriceCents(row.type, prices) : (EVENT_PLAN_PRICE_CENTS[row.type] ?? 0),
    paid: false,
  };
}

// Applies a validated coupon to a cents amount so the UI can show the
// discounted price the user will actually be charged (backend remains the
// source of truth — hence callers prefix "~"). amountOff is in cents;
// validate-coupon already rejects a currency mismatch, so no currency
// conversion is needed here.
export function applyCouponCents(
  cents: number,
  coupon: CouponValidation,
): number {
  if (coupon.percentOff) return Math.round(cents * (1 - coupon.percentOff / 100));
  if (coupon.amountOff) return Math.max(0, cents - coupon.amountOff);
  return cents;
}
